import { flow, isUndefined, set } from 'lodash/fp';
import { Reducer } from 'redux';

import { LoginActionTypes } from '../../../auth/login/store/types';
import { MyRiskTolerance, RiskTolerance } from '../../risk-tolerance/store/types';
import { ClientRiskToleranceActionTypes, ClientRiskToleranceView, ClientRiskToleranceState } from './types';
import { updateCurrencyAmountRiskToleranceConfig, updateRiskToleranceConfig } from '../../risk-tolerance/utils';

export const INITIAL_STATE: ClientRiskToleranceState = {
    fetchingAllDatasets: false,
    allDatasets: [],
    riskToleranceView: ClientRiskToleranceView.LIST,
    selectedDataset: null,
    datasetDefinitions: [],
    hiddenFields: {},
    isSaving: false,
    clientRiskToleranceConfig: null,
    currentRiskToleranceConfig: null,
    riskToleranceIsRecalculating: false,
    riskToleranceIncludesOverrides: false,
    currentRiskField: null,
    riskToleranceUpdated: false,
    unsavedChangesModalOpen: false,
    fetchingDatasetDefinitions: false,
    documentHiddenFields: [],
    riskFieldsUpdatedModalOpen: false,
    riskFieldsUpdated: []
};

export const clientRiskToleranceReducer: Reducer<ClientRiskToleranceState> = (state = INITIAL_STATE, { type, payload }): ClientRiskToleranceState => {
    switch (type) {
        case ClientRiskToleranceActionTypes.FETCH_ALL_CLIENT_RISK_TOLERANCE_DATASETS_STARTED:
            return set('fetchingAllDatasets', true, state);
        case ClientRiskToleranceActionTypes.FETCH_ALL_CLIENT_RISK_TOLERANCE_DATASETS_SUCCESSFUL:
            return flow(
                set('allDatasets', payload),
                set('fetchingAllDatasets', false)
            )(state);
        case ClientRiskToleranceActionTypes.FETCH_ALL_CLIENT_RISK_TOLERANCE_DATASETS_FAILED:
            return set('fetchingAllDatasets', false, state);
        case ClientRiskToleranceActionTypes.CLIENT_RISK_TOLERANCE_VIEW: {
            const { view, datasetId } = payload;
            return flow(
                set('riskToleranceView', view),
                set('selectedDataset', datasetId),
                set('unsavedChangesModalOpen', false),
                set('riskToleranceUpdated', false)
            )(state);
        }
        case ClientRiskToleranceActionTypes.FETCH_CLIENT_RISK_TOLERANCE_DATASET_DEFINITIONS_STARTED:
            return flow(
                set('fetchingDatasetDefinitions', true),
                set('datasetDefinitions', []),
                set('hiddenFields', {}),
                set('clientRiskToleranceConfig', null),
                set('currentRiskToleranceConfig', null),
                set('documentHiddenFields', [])
            )(state);
        case ClientRiskToleranceActionTypes.FETCH_CLIENT_RISK_TOLERANCE_DATASET_DEFINITIONS_SUCCESSFUL: {
            const { datasetDefinitions, hiddenFields, riskTolerance, documentHiddenFields, riskToleranceIsRecalculating, riskToleranceIncludesOverrides } = payload;
            return flow(
                set('datasetDefinitions', datasetDefinitions),
                set('hiddenFields', hiddenFields),
                set('clientRiskToleranceConfig', riskTolerance),
                set('currentRiskToleranceConfig', riskTolerance),
                set('documentHiddenFields', documentHiddenFields),
                set('fetchingDatasetDefinitions', false),
                set('riskToleranceIsRecalculating', riskToleranceIsRecalculating),
                set('riskToleranceIncludesOverrides', riskToleranceIncludesOverrides)
            )(state);
        }
        case ClientRiskToleranceActionTypes.FETCH_CLIENT_RISK_TOLERANCE_DATASET_DEFINITIONS_FAILED:
            return set('fetchingDatasetDefinitions', false, state);
        case ClientRiskToleranceActionTypes.SET_CURRENT_RISK_TOLERANCE_FIELD:
            return set('currentRiskField', payload, state);
        case ClientRiskToleranceActionTypes.UPDATE_RISK_FIELD_CONFIG: {
            const { riskAssociated, value, selectedCurrency } = payload;
            const { datasetId, fieldId } = state.currentRiskField!;
            let updatedRiskToleranceConfig: (RiskTolerance | MyRiskTolerance)[] = [];
            if (!isUndefined(selectedCurrency)) {
                updatedRiskToleranceConfig = updateCurrencyAmountRiskToleranceConfig(state.currentRiskToleranceConfig!, datasetId, fieldId, selectedCurrency, riskAssociated, value) as RiskTolerance[];
            } else {
                updatedRiskToleranceConfig = updateRiskToleranceConfig(state.currentRiskToleranceConfig!, datasetId, fieldId, riskField => set(`riskConfig[${riskAssociated}]`, value, riskField));
            }
            return set('currentRiskToleranceConfig', updatedRiskToleranceConfig, state);
        }
        case ClientRiskToleranceActionTypes.UPDATE_RISK_FIELD_WEIGHTING: {
            const { datasetId, fieldId } = state.currentRiskField!;
            const updatedRiskToleranceConfig = updateRiskToleranceConfig(state.currentRiskToleranceConfig!, datasetId, fieldId, riskField => set('weighting', payload, riskField));
            return set('currentRiskToleranceConfig', updatedRiskToleranceConfig, state);
        }
        case ClientRiskToleranceActionTypes.UPDATE_RISK_FIELD_FALLBACK_RISK_POSITION: {
            const { datasetId, fieldId } = state.currentRiskField!;
            const updatedRiskToleranceConfig = updateRiskToleranceConfig(state.currentRiskToleranceConfig!, datasetId, fieldId, riskField => set('fallbackRiskPosition', payload, riskField));
            return set('currentRiskToleranceConfig', updatedRiskToleranceConfig, state);
        }
        case ClientRiskToleranceActionTypes.TOGGLE_CLIENT_RISK_FIELD: {
            const { datasetId, fieldId } = payload;
            const updatedRiskTolerance = updateRiskToleranceConfig(state.currentRiskToleranceConfig!, datasetId, fieldId, riskField => set('includeRiskField', !riskField.includeRiskField, riskField));
            return set('currentRiskToleranceConfig', updatedRiskTolerance, state);
        }
        case ClientRiskToleranceActionTypes.SET_RISK_TOLERANCE_UPDATED:
            return set('riskToleranceUpdated', payload, state);
        case ClientRiskToleranceActionTypes.SAVE_CLIENT_RISK_TOLERANCE_STARTED:
            return set('isSaving', true, state);
        case ClientRiskToleranceActionTypes.SAVE_CLIENT_RISK_TOLERANCE_SUCCESSFUL:
            return flow(
                set('isSaving', false),
                set('riskToleranceConfig', payload.newRiskTolerance),
                set('currentRiskToleranceConfig', payload.newRiskTolerance),
                set('riskToleranceIsRecalculating', payload.isRecalculating),
                set('riskToleranceUpdated', false),
                set('riskFieldsUpdatedModalOpen', false),
                set('riskFieldsUpdated', [])
            )(state);
        case ClientRiskToleranceActionTypes.SAVE_CLIENT_RISK_TOLERANCE_FAILED:
            return set('isSaving', false, state);
        case ClientRiskToleranceActionTypes.TOGGLE_UNSAVED_CHANGES_MODAL:
            return set('unsavedChangesModalOpen', payload, state);
        case ClientRiskToleranceActionTypes.SET_DATASET_DOCUMENT_RISK_TOLERANCE_RECALCULATING: {
            const { datasetId, isRecalculating } = payload;
            if (datasetId === state.selectedDataset) {
                return set('riskToleranceIsRecalculating', isRecalculating, state);
            }
            return state;
        }
        case ClientRiskToleranceActionTypes.TOGGLE_RISK_FIELDS_UPDATED_MODAL:
            return set('riskFieldsUpdatedModalOpen', payload, state);
        case ClientRiskToleranceActionTypes.SET_RISK_TOLERANCE_RISK_FIELDS_UPDATED:
            return set('riskFieldsUpdated', payload, state);
        case ClientRiskToleranceActionTypes.RESET_CLIENT_RISK_TOLERANCE_DATASETS:
        case LoginActionTypes.LOGOUT_SUCCESSFUL:
            return INITIAL_STATE;
        default:
            return state;
    }
};
